import { Injectable, NotFoundException, BadRequestException } from '@nestjs/common';
import { InjectModel } from '@nestjs/mongoose';
import { Model, Types } from 'mongoose';
import { Coupon, CouponDocument, CouponType } from '@/modules/coupon/schemas/coupon.schema';
import { CreateCouponDto } from '../dto/create-coupon.dto';
import { UpdateCouponDto } from '../dto/update-coupon.dto';
import { CountryService } from '@/modules/location/services/country.service';
import { StateService } from '@/modules/location/services/state.service';

@Injectable()
export class CouponService {
  constructor(
    @InjectModel(Coupon.name) private couponModel: Model<CouponDocument>,
    private readonly countryService: CountryService,
    private readonly stateService: StateService
  ) {}

  async create(createCouponDto: CreateCouponDto): Promise<Coupon> {
    const createdCoupon = new this.couponModel(createCouponDto);
    return createdCoupon.save();
  }

  async findAll(): Promise<Coupon[]> {
    return this.couponModel.find().exec();
  }

  async findById(id: string): Promise<Coupon> {
    return this.couponModel.findById(id).exec();
  }

  async findOne(id: string): Promise<Coupon> {
    const coupon = await this.couponModel.findById(id).exec();
    if (!coupon) {
      throw new NotFoundException(`Coupon with ID ${id} not found`);
    }
    return coupon;
  }

  async incrementUsage(id: string): Promise<CouponDocument> {
    const coupon = await this.couponModel.findById(id);
    if (!coupon) {
      throw new Error('Coupon not found');
    }
    coupon.usedCount++;
    return await coupon.save();
  }

  async decrementUsage(id: string): Promise<CouponDocument> {
    const coupon = await this.couponModel.findById(id);
    if (!coupon) {
      throw new Error('Coupon not found');
    }
    coupon.usedCount = Math.max(0, coupon.usedCount - 1);
    return await coupon.save();
  }

  async update(id: string, updateCouponDto: UpdateCouponDto): Promise<Coupon> {
    const coupon = await this.couponModel.findByIdAndUpdate(
      id,
      updateCouponDto,
      { new: true }
    ).exec();

    if (!coupon) {
      throw new NotFoundException(`Coupon with ID ${id} not found`);
    }

    return coupon;
  }

  async remove(id: string): Promise<void> {
    const result = await this.couponModel.findByIdAndDelete(id).exec();
    if (!result) {
      throw new NotFoundException(`Coupon with ID ${id} not found`);
    }
  }

  async findByCode(code: string): Promise<Coupon> {
    const coupon = await this.couponModel.findOne({ code: code.toUpperCase() }).exec();
    if (!coupon) {
      throw new NotFoundException(`Coupon with code ${code} not found`);
    }
    return coupon;
  }

  async validatePromoCode(
    code: string,
    subtotal: number,
    shippingCost: number = 0,
    userId?: string,
    countryId?: string,
    stateId?: string
  ): Promise<{
    valid: boolean;
    coupon?: Coupon;
    discount: number;
    shippingDiscount: number;
    message?: string;
  }> {
    try {
      const coupon = await this.findByCode(code);
      
      // Check if coupon is valid
      const now = new Date();
      const isValidCoupon = (
        coupon.isActive &&
        (!coupon.startDate || new Date(coupon.startDate) <= now) &&
        (!coupon.expiryDate || new Date(coupon.expiryDate) >= now) &&
        coupon.usedCount < coupon.usageLimit
      );

      if (!isValidCoupon) {
        let message = 'Promo code is invalid';
        if (!coupon.isActive) {
          message = 'Promo code is inactive';
        } else if (coupon.expiryDate && new Date(coupon.expiryDate) < now) {
          message = 'Promo code has expired';
        } else if (coupon.startDate && new Date(coupon.startDate) > now) {
          message = 'Promo code is not yet active';
        } else if (coupon.usedCount >= coupon.usageLimit) {
          message = 'Promo code has reached its usage limit';
        }
        
        return {
          valid: false,
          discount: 0,
          shippingDiscount: 0,
          message
        };
      }

      // Check if user has already used this coupon
      if (userId && coupon.usedByUsers && coupon.usedByUsers.some(id => id.toString() === userId)) {
        return {
          valid: false,
          discount: 0,
          shippingDiscount: 0,
          message: 'You have already used this promo code'
        };
      }

      // Check if country has states and state is required for shipping discount
      if (countryId && coupon.isShippingDiscount) {
        try {
          const states = await this.stateService.findByCountry(countryId);
          if (states && states.length > 0 && !stateId) {
            return {
              valid: false,
              discount: 0,
              shippingDiscount: 0,
              message: 'يجب اختيار المحافظة لتطبيق خصم الشحن'
            };
          }
        } catch (error) {
          console.warn(`[CouponService] Could not validate state requirement: ${error.message}`);
          // Continue with validation if state service fails
        }
      }

      // Check minimum order amount
      if (coupon.minimumOrderAmount && subtotal < coupon.minimumOrderAmount) {
        return {
          valid: false,
          discount: 0,
          shippingDiscount: 0,
          message: `Minimum order amount is ${coupon.minimumOrderAmount}`
        };
      }

      let discount = 0;
      let shippingDiscount = 0;

      if (coupon.isShippingDiscount) {
        // This is a shipping discount coupon
        if (coupon.type === CouponType.PERCENTAGE) {
          shippingDiscount = Math.min((shippingCost * coupon.value) / 100, shippingCost);
        } else if (coupon.type === CouponType.FIXED_AMOUNT) {
          // If value is 0, treat as full shipping discount
          if (coupon.value === 0) {
            shippingDiscount = shippingCost;
          } else {
            shippingDiscount = Math.min(coupon.value, shippingCost);
          }
        }
      } else {
        // This is a regular discount coupon
        if (coupon.type === CouponType.PERCENTAGE) {
          discount = (subtotal * coupon.value) / 100;
        } else if (coupon.type === CouponType.FIXED_AMOUNT) {
          discount = coupon.value;
        }

        // Apply maximum discount limit if set
        if (coupon.maximumDiscountAmount && discount > coupon.maximumDiscountAmount) {
          discount = coupon.maximumDiscountAmount;
        }

        // Don't exceed subtotal
        discount = Math.min(discount, subtotal);
      }

      return {
        valid: true,
        coupon,
        discount,
        shippingDiscount,
        message: 'Promo code applied successfully'
      };
    } catch (error) {
      return {
        valid: false,
        discount: 0,
        shippingDiscount: 0,
        message: error.message || 'Invalid promo code'
      };
    }
  }
}
